import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    CreateMonitorProfileScheduleInput,
    useCreateMonitorProfileScheduleMutation,
    useSearchAccountsQuery
} from '../../api/client'
import PageMasthead from '../../components/PageMasthead'
import ComboboxMenu from '../../components/inputs/ComboBox'
import { ComboboxMenuOption } from '../../components/inputs/ComboboxMenuOption'
import { toastError, toastSuccess } from '../../components/Notification'
import MonitorScheduleForm, {
    MonitorScheduleValues
} from '../../components/scheduledJobs/MonitorScheduleForm'
import { PLATFORMS, PlatformConfig } from '../../components/scheduledJobs/platforms'
import { SCRAPE_COMMENT_FETCH_KIND } from '../../components/scheduledJobs/tags'

/**
 * The schedule types this page can create. For future jobs you can
 * add an entry here and its params form below.
 */
const SCHEDULE_TYPES = [{ value: SCRAPE_COMMENT_FETCH_KIND, label: 'Monitor' }] as const

/**
 * Create a Monitor: a recurring Shino job that scrapes
 * one monitored account's comments. Admin-only, like the mutation.
 */
function ScheduledJobCreatePage() {
    const navigate = useNavigate()
    const [platform, setPlatform] = useState<PlatformConfig>(PLATFORMS[0])
    const [accountQuery, setAccountQuery] = useState('')
    const [selectedAccount, setSelectedAccount] = useState<ComboboxMenuOption | undefined>()
    const [typedUsername, setTypedUsername] = useState('')

    const {
        data: accountsData,
        loading: accountsLoading,
        error: accountsError
    } = useSearchAccountsQuery({
        variables: {
            params: {
                offset: 0,
                limit: 500,
                keyword: '',
                socialMediaServices: [platform.searchEnum],
                teamIds: []
            }
        }
    })

    const selectableAccounts = useMemo(() => {
        const accounts = accountsData?.searchAccounts?.accounts ?? []
        return accounts
            .filter((account) => account.serviceAccountUsername)
            .slice()
            .sort((a, b) => {
                if (a.hasAuthorisation !== b.hasAuthorisation) {
                    return a.hasAuthorisation ? 1 : -1
                }
                return (a.serviceAccountUsername ?? '').localeCompare(
                    b.serviceAccountUsername ?? ''
                )
            })
    }, [accountsData])

    /** The option label: handle, then the account name, then whether it is authorised. */
    const optionLabel = (account: (typeof selectableAccounts)[number]) =>
        [
            account.serviceAccountUsername,
            account.accountName && account.accountName !== account.serviceAccountUsername
                ? `— ${account.accountName}`
                : null,
            account.hasAuthorisation ? '(authorised)' : null
        ]
            .filter(Boolean)
            .join(' ')

    const accountOptions: ComboboxMenuOption[] = useMemo(() => {
        const needle = accountQuery.trim().toLowerCase()
        return selectableAccounts
            .filter((account) => {
                if (!needle) return true
                const haystack = `${account.serviceAccountUsername ?? ''} ${
                    account.accountName ?? ''
                }`.toLowerCase()
                return haystack.includes(needle)
            })
            .map((account) => ({ id: account.id, name: optionLabel(account) }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectableAccounts, accountQuery])

    const serviceAccountUsername = selectedAccount
        ? (selectableAccounts.find((candidate) => candidate.id === selectedAccount.id)
              ?.serviceAccountUsername ?? '')
        : typedUsername.trim()

    // Switching platform reloads the account list, so a selection made under
    // the old platform is no longer meaningful.
    useEffect(() => {
        setSelectedAccount(undefined)
        setAccountQuery('')
        setTypedUsername('')
    }, [platform])

    const [createSchedule, { loading: creating }] = useCreateMonitorProfileScheduleMutation({
        onCompleted: (data) => {
            toastSuccess(`Monitoring schedule ${data.createMonitorProfileSchedule.tag} created`)
            navigate('/admin/scheduled-jobs')
        },
        onError: (error) => {
            // The backend's messages are user-ready, e.g. "no instagram
            // account found for username 'x' — create the monitored
            // account first".
            toastError(error.message)
        }
    })

    const selectPlatform = (wireValue: string) => {
        const next = PLATFORMS.find((p) => p.wireValue === wireValue)
        if (next) setPlatform(next)
    }

    // No guard on the account here: the form disables its submit until
    // `hasAccount`, and that is fed by both the combobox and the typed-handle
    // fallback, so this cannot be reached without one. The old page validated
    // on the way through instead, and deliberately kept its button live —
    // that rationale moved with the button into MonitorScheduleForm.
    const onSubmit = (values: MonitorScheduleValues) => {
        const input: CreateMonitorProfileScheduleInput = {
            platform: platform.wireValue,
            serviceAccountUsername,
            ...values
        }
        createSchedule({ variables: { input } })
    }

    const inputClasses =
        'mt-1 block w-full border border-ink-300 rounded-md shadow-sm focus:ring-arwen-500 focus:border-arwen-500 sm:text-sm'
    const labelClasses = 'block text-sm font-medium text-ink-700'
    const hintClasses = 'text-xs text-ink-500 mt-1'

    const newMonitoredAccountLink = (
        <Link
            to={`/account/new/monitored/${platform.monitoredRouteKey}`}
            className="text-arwen-600 underline hover:text-arwen-800"
        >
            create a new monitored account
        </Link>
    )

    const accountField = (
        <div>
            {/* A searchable combobox, not free text and not a plain select.
                The account must already exist — the backend looks it up and
                refuses otherwise — so free text could only produce a handle
                that happens to match or one that fails on submit. And a plain
                select is unusable at real volume.

                The one case for a text box is a failed query: if the list
                could not be loaded, typing the handle beats being stuck. */}
            {accountsError ? (
                <>
                    <label className={labelClasses} htmlFor="serviceAccountUsername">
                        Monitored account
                    </label>
                    <input
                        id="serviceAccountUsername"
                        type="text"
                        className={inputClasses}
                        placeholder="Exact handle, e.g. hotelsdotcom"
                        value={typedUsername}
                        onChange={(e) => setTypedUsername(e.target.value)}
                    />
                    <p className={hintClasses}>
                        The account list could not be loaded — enter the exact handle instead.
                    </p>
                </>
            ) : (
                <ComboboxMenu
                    label="Monitored account"
                    options={accountOptions}
                    value={selectedAccount}
                    onChange={setSelectedAccount}
                    onQueryChange={setAccountQuery}
                    isLoading={accountsLoading}
                    canCreate={false}
                />
            )}
            <p className={hintClasses}>
                The account must already exist in Arwen — {newMonitoredAccountLink} if it isn&apos;t
                listed. Monitoring is meant for accounts Arwen has no authorisation for; authorised
                ones are marked, and their comments already arrive through the platform&apos;s own
                API, so scraping them usually duplicates work you already have.
            </p>
        </div>
    )

    return (
        <div className="min-h-screen bg-ink-50">
            <PageMasthead
                title="New scheduled job"
                tabTitle="New scheduled job"
                subtitle="Create a recurring job on the Shino scheduler"
            />
            <div className="m-10 space-y-6 w-2/3 max-w-3xl">
                <p className="text-sm text-ink-500">
                    Monitoring schedules collect a monitored account&apos;s social media comments on
                    a daily cadence.
                </p>
                <div className="space-y-4 bg-white shadow rounded-lg p-6">
                    <div>
                        <label className={labelClasses} htmlFor="schedule-type">
                            Schedule type
                        </label>
                        <select id="schedule-type" className={inputClasses}>
                            {SCHEDULE_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                        <p className={hintClasses}>
                            A Monitor scrapes comments from an account Arwen has no
                            authorisation for.
                        </p>
                    </div>

                    <div>
                        <label className={labelClasses} htmlFor="platform">
                            Platform
                        </label>
                        <select
                            id="platform"
                            className={inputClasses}
                            value={platform.wireValue}
                            onChange={(e) => selectPlatform(e.target.value)}
                        >
                            {PLATFORMS.map((p) => (
                                <option key={p.wireValue} value={p.wireValue}>
                                    {p.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Keyed on the platform so switching it starts a clean
                        form: the mode a platform supports, and the fields that
                        go with it, both change. */}
                    <MonitorScheduleForm
                        key={platform.wireValue}
                        platform={platform}
                        accountField={accountField}
                        hasAccount={Boolean(serviceAccountUsername)}
                        submitLabel="Create schedule"
                        submitting={creating}
                        onSubmit={onSubmit}
                        onCancel={() => navigate('/admin/scheduled-jobs')}
                    />
                </div>
            </div>
        </div>
    )
}

export default ScheduledJobCreatePage
